import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';

import Breadcrumbs from '../../../../layout/components/Breadcrumbs';
import Text from '../../../../components/Text/Text';
import Button from '../../../../components/Button/Button';
import ButtonTypes from '../../../../constants/buttonTypes';
import {
  IconCalculator, IconFileExport, IconPlus, IconSetting, IconTrack
} from '../../../../assets/icon';
import TableSettingModal from '../../../../components/TableSettingModal/TableSettingModal';
import toggleArrayItem from '../../../../helpers/toggleArrayItem';
import { coOrdersTable } from '../../../../constants/coOrdersTable';
import { setApplicationState } from '../../../../actions/application/application';
import coOrderAssemblyType from '../../../../constants/coOrderAssemblyType';
import coOrderStatus from '../../../../constants/coOrderStatus';
import { setCoOrdersFilter } from '../../../../actions/coilovers/orders/coOrdersFilter';
import getCoOrders from '../../../../thunks/coilovers/orders/getCoOrders';
import Badge from '../../../../components/Badge/Badge';
import downloadFilesType from '../../../../constants/downloadFilesType';
import DownloadFileModal from '../../../../components/DownloadFileModal/DownloadFileModal';
import PermissionsGate from '../../../../components/PermissionsGate/PermissionsGate';
import { permissions } from '../../../../common/permissionGroup';
import CalculateReportModal from '../../../../components/CalculateReportModal/CalculateReportModal';
import FedexPickupModal from '../../../../components/FedexPickupModal/FedexPickupModal';
import CoOrderNewOrderModal from '../../../../components/CoOrderNewOrderModal/CoOrderNewOrderModal';

const subheaderClassName = 'flex items-center border-t border-[#eff2f5] dark:border-[#1b263b] px-[25px] h-full';
const breadcrumbsClassName = 'py-[5px] max-[1200px]:hidden';
const titleClassName = 'text-text-second text-[13px] mr-[10px]';
const badgeClassName = 'p-[5px]';
const buttonClassName = 'px-[13px] py-[8px] mr-[5px]';
const dividerClassName = 'w-[1px] bg-[#eff2f5] dark:bg-[#1b263b] mx-[17px]';

const CoOrderSubheader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tableSettingModalVisible, setTableSettingModalVisible] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [newOrderModalVisible, setNewOrderModalVisible] = useState(false);
  const [requestPickupModalVisible, setRequestPickupModalVisible] = useState(false);

  const {
    application: { coOrderTableSetting },
    getCoOrders: { total },
    coOrdersFilter,
  } = useSelector((state) => ({
    coOrdersFilter: state?.coOrdersFilter,
    application: state?.application,
    getCoOrders: state?.getCoOrders,
  }), shallowEqual);

  const breadcrumbs = {
    pageTitle: 'Orders',
    history: [
      {
        title: 'Home',
        onClick: () => navigate('/')
      },
      {
        title: 'Coilovers',
      },
      {
        title: 'Orders',
      },
    ]
  };

  const onOrderIssue = () => {
    batch(() => {
      dispatch(setCoOrdersFilter({
        orderStatus: [],
        orderAssemblyType: [
          coOrderAssemblyType.ORDER_ISSUE
        ],
        order: 'order_assembly_urgency:desc',
        page: 1
      }));
      dispatch(getCoOrders());
    });
  };

  const onReady = () => {
    batch(() => {
      dispatch(setCoOrdersFilter({
        orderStatus: [coOrderStatus.READY_ASB, coOrderStatus.PICKED],
        orderAssemblyType: [
          coOrderAssemblyType.ASSEMBLY,
          coOrderAssemblyType.OVERSTOCK,
        ],
        orderAssemblyStyle: [],
        order: 'order_assembly_urgency:desc',
        page: 1
      }));
      dispatch(getCoOrders());
    });
  };

  const onAssembly = () => {
    batch(() => {
      dispatch(setCoOrdersFilter({
        orderStatus: [coOrderStatus.PART_BOX],
        orderAssemblyType: [
          coOrderAssemblyType.ASSEMBLY,
          coOrderAssemblyType.OVERSTOCK
        ],
        orderAssemblyStyle: [],
        order: 'order_assembly_urgency:desc',
        page: 1
      }));
      dispatch(getCoOrders());
    });
  };

  const onPacking = () => {
    batch(() => {
      dispatch(setCoOrdersFilter({
        orderStatus: [coOrderStatus.PACK_BOX],
        orderAssemblyType: [
          coOrderAssemblyType.ASSEMBLY,
          coOrderAssemblyType.OVERSTOCK,
        ],
        orderAssemblyStyle: [],
        order: 'order_assembly_urgency:desc',
        page: 1
      }));
      dispatch(getCoOrders());
    });
  };

  const onSending = () => {
    batch(() => {
      dispatch(setCoOrdersFilter({
        orderStatus: [
          coOrderStatus.READY_ASB,
          coOrderStatus.OUT_OF_STOCK,
          coOrderStatus.OUT_OF_RESERVE,
          coOrderStatus.PART_BOX,
          coOrderStatus.PICKED,
          coOrderStatus.PACK_BOX
        ],
        orderAssemblyType: [coOrderAssemblyType.SENDING],
        order: 'order_assembly_urgency:desc',
        page: 1
      }));
      dispatch(getCoOrders());
    });
  };

  return (
    <div className={subheaderClassName}>
      <div className="flex-1">
        <Breadcrumbs breadcrumbs={breadcrumbs} containerStyle={breadcrumbsClassName} />
      </div>
      <div className="flex flex-row justify-end">
        <div className="flex flex-row items-center">
          <Text text="Filtered:" textStyle={titleClassName} />
          <Badge
            text={`${total}${total >= 10000 ? '+' : ''}`}
            type={ButtonTypes.LIGHT_INFO}
            containerStyle={badgeClassName}
          />
        </div>
        <div className={dividerClassName} />
        <div className="flex flex-row">
          <Text bold text="Filter By:" textStyle={titleClassName} />
          <Button
            text="Ready"
            type={ButtonTypes.ACTIVE_LIGHT}
            containerStyle={buttonClassName}
            onClick={onReady}
          />
          <Button
            text="Assembly"
            type={ButtonTypes.ACTIVE_LIGHT}
            containerStyle={buttonClassName}
            onClick={onAssembly}
          />
          <Button
            text="Packing"
            type={ButtonTypes.ACTIVE_LIGHT}
            containerStyle={buttonClassName}
            onClick={onPacking}
          />
          <Button
            text="Sending"
            type={ButtonTypes.ACTIVE_LIGHT}
            containerStyle={buttonClassName}
            onClick={onSending}
          />
          <Button
            text="Issue"
            type={ButtonTypes.ACTIVE_LIGHT}
            containerStyle={buttonClassName}
            onClick={onOrderIssue}
          />
        </div>
        <div className={dividerClassName} />
        <Button
          type={ButtonTypes.LIGHT_SUCCESS}
          icon={<IconPlus />}
          onClick={() => setNewOrderModalVisible(true)}
          containerStyle={buttonClassName}
          tooltip="Add new order"
          tooltipId="addNewOrder"
        />
        <Button
          type={ButtonTypes.LIGHT}
          icon={<IconTrack />}
          containerStyle={buttonClassName}
          tooltip="Request pickup"
          tooltipId="request_pickup"
          onClick={() => setRequestPickupModalVisible(true)}
        />
        <Button
          type={ButtonTypes.LIGHT}
          icon={<IconFileExport />}
          containerStyle={buttonClassName}
          tooltip="Click to download"
          tooltipId="coOrderExportTable"
          onClick={() => setDownloadModalVisible(true)}
        />
        <PermissionsGate can={permissions.ADMIN_ADMIN}>
          <Button
            type={ButtonTypes.LIGHT}
            icon={<IconCalculator />}
            containerStyle={buttonClassName}
            tooltip="Calculate report"
            tooltipId="calculateReport"
            onClick={() => setReportModalVisible(true)}
          />
        </PermissionsGate>
        <Button
          type={ButtonTypes.LIGHT}
          icon={<IconSetting />}
          containerStyle={buttonClassName}
          tooltip="Table settings"
          tooltipId="coOrderTableSettings"
          onClick={() => setTableSettingModalVisible(true)}
        />
      </div>
      <TableSettingModal
        title="Order Table Setting"
        visible={tableSettingModalVisible}
        toggleVisible={() => setTableSettingModalVisible(false)}
        list={coOrdersTable}
        values={coOrderTableSetting}
        onSelect={(val) => {
          dispatch(setApplicationState({
            coOrderTableSetting: toggleArrayItem(coOrderTableSetting, val)
          }));
        }}
      />
      <DownloadFileModal
        visible={downloadModalVisible}
        toggleVisible={() => setDownloadModalVisible(false)}
        type={downloadFilesType.MM_ASSEMBLY_ORDER}
        filter={coOrdersFilter}
      />
      <CoOrderNewOrderModal
        visible={newOrderModalVisible}
        toggleVisible={() => setNewOrderModalVisible(!newOrderModalVisible)}
      />
      <CalculateReportModal
        visible={reportModalVisible}
        toggleVisible={() => setReportModalVisible(!reportModalVisible)}
      />
      <FedexPickupModal
        visible={requestPickupModalVisible}
        toggleVisible={() => setRequestPickupModalVisible(!requestPickupModalVisible)}
      />
    </div>
  );
};

CoOrderSubheader.propTypes = {};

CoOrderSubheader.defaultProps = {};

export default CoOrderSubheader;
